import React, { useState, useEffect } from 'react';
import config from '../config';
import { NavLink } from 'react-router-dom';
import styled from 'styled-components';
import { MdClose, MdMenu } from 'react-icons/md';
import WeatherBadge from './WeatherBadge';
import PortfolioIcon from './PortfolioIcon';

const BrandLink = styled(NavLink)`
  position: absolute;
  left: 1.5rem;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  padding: 0 !important;
  line-height: 0;
  &:hover svg {
    filter: drop-shadow(0 0 6px var(--highlight-blue));
  }
  @media only screen and (max-width: 768px) {
    top: 0.6rem;
    transform: none;
    svg {
      width: 36px !important;
      height: 36px !important;
    }
  }
`;

const NavStyles = styled.nav`
  position: fixed;
  z-index: 100;
  top: 0;
  left: 0;
  width: 100%;
  padding: 1rem 0;
  background: transparent;
  transition: padding 0.3s ease, box-shadow 0.3s ease;

  /* Frosted-glass layer — opacity animates on scroll */
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: var(--dark-bg);
    opacity: 1;
    transition: opacity 0.3s ease;
    z-index: -1;
  }

  &.scrolled {
    padding: 0.4rem 0;
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
    box-shadow: 0 2px 20px rgba(0, 0, 0, 0.25);
    &::before {
      opacity: 0.85;
    }
  }

  ul {
    max-width: 1200px;
    margin: 0 auto;
    width: 90%;
    text-align: center;
    li {
      display: inline-block;
      border-radius: 8px;
      transition: 0.3s ease background-color;
      &:hover {
        background-color: var(--deep-dark);
      }
    }
    a {
      display: inline-block;
      font-family: 'RobotoMono Regular';
      padding: 1rem 2rem;
      font-size: 2rem;
      color: var(--gray-1);
      outline: none;
    }
    .active {
      color: var(--highlight-blue);
    }
  }

  .mobile-menu-icon {
    position: absolute;
    right: 1rem;
    top: 1rem;
    width: 4rem;
    font-size: 3.2rem;
    color: var(--gray-1);
    cursor: pointer;
    display: none;
    outline: none;
    * {
      pointer-events: none;
    }
  }

  .navItems .closeNavIcon {
    display: none;
  }

  @media only screen and (max-width: 768px) {
    padding: 0;
    min-height: calc(3.2rem + 2rem);
    .hide-item {
      transform: translateY(calc(-100% - var(--top)));
    }
    .mobile-menu-icon {
      display: block;
    }
    .navItems {
      --top: 1rem;
      transition: 0.3s ease transform;
      background-color: var(--deep-dark);
      padding: 2rem;
      width: 90%;
      max-width: 300px;
      border-radius: 12px;
      position: absolute;
      right: 1rem;
      top: var(--top);
      .closeNavIcon {
        display: block;
        width: 3rem;
        margin: 0 0 0 auto;
        cursor: pointer;
        * {
          pointer-events: none;
        }
      }
      li {
        display: block;
        margin-bottom: 1rem;
      }
    }
  }
`;

export default function NavMenu() {
  const [showNav, setShowNav] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 80);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const closeNav = () => setShowNav(false);
  const toggleNav = () => setShowNav((v) => !v);

  return (
    <NavStyles className={scrolled ? 'scrolled' : ''}>
      <BrandLink to="/" exact aria-label="Home">
        <PortfolioIcon size={48} showIcons={false} />
      </BrandLink>
      <div
        className="mobile-menu-icon"
        onClick={toggleNav}
        role="button"
        aria-label="Open navigation menu"
        onKeyDown={(e) => e.key === 'Enter' && toggleNav()}
        tabIndex={0}
      >
        <MdMenu />
      </div>

      <ul className={!showNav ? 'navItems hide-item' : 'navItems'}>
        <div
          className="closeNavIcon"
          onClick={closeNav}
          role="button"
          aria-label="Close navigation menu"
          onKeyDown={(e) => e.key === 'Enter' && closeNav()}
          tabIndex={0}
        >
          <MdClose />
        </div>
        <li>
          <NavLink to="/" exact onClick={closeNav}>Home</NavLink>
        </li>
        <li>
          <NavLink to="/about" onClick={closeNav}>About</NavLink>
        </li>
        {config.projectsSectionEnabled && (
          <li>
            <NavLink to="/projects" onClick={closeNav}>Projects</NavLink>
          </li>
        )}
        {config.blogEnabled && (
          <li>
            <NavLink to="/blog" onClick={closeNav}>Blog</NavLink>
          </li>
        )}
        <li>
          <NavLink to="/contact" onClick={closeNav}>Contact</NavLink>
        </li>
        <li>
          <NavLink to="/resume" onClick={closeNav}>Resume</NavLink>
        </li>
      </ul>
      <WeatherBadge />
    </NavStyles>
  );
}
